<template>
  <!--This action is always displayed on action sheet-->
  <BaseButtonDropdown
    ref="button"
    class="column"
    :close-on-click="close"
    :placement="placement"
    submenu
    @focusin="calculatePlacement"
    @hide="onHide"
    @mouseenter="calculatePlacement"
  >
    <template #btn="{ listeners }">
      <BaseButton
        action-item
        :class="baseButtonClass"
        data-testid="tag"
        icon="tag-mention"
        :icon-size="iconSize"
        :label="actionLabel"
        :label-class="labelClass"
        new-design
        new-icon
        v-on="listeners"
      />
    </template>

    <DropdownBookmarkMentions
      data-testid="dropdown-bookmark-mentions"
      :ids="[`${mention.type}_${mention.id}`]"
      :stream="sourceStream"
      :title="$t('dropdown_bookmark_mentions.tag_mention')"
      @add-new-bookmark-stream="addBookmarkStreamToGroup"
      @close="cancel"
      @move-to-existing-group="moveToExistingGroup"
      @update-bookmark-streams="updateBookmarkStreams"
    />
  </BaseButtonDropdown>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton, BaseButtonDropdown } from "shared/components/base";
import DropdownBookmarkMentions from "shared/components/DropdownBookmarkMentions.vue";
import useBookmarks from "shared/composables/useBookmarks";
import useProductMenu from "shared/composables/useProductMenu";
import useTeamPicker from "shared/composables/useTeamPicker";
import { streamTypes } from "shared/constants";
import { useStreamGroupsStore } from "shared/stores/streamGroups";

export default {
  name: "BookmarkStreamActions",
  components: {
    BaseButton,
    BaseButtonDropdown,
    DropdownBookmarkMentions,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    sourceStream: {
      type: Object,
      default: null,
    },
    product: {
      type: String,
      default: null,
    },
    isSocial: {
      type: Boolean,
    },
    iconSize: {
      type: String,
      default: "20px",
    },
    labelClass: {
      type: [Array, Object, String],
      default: "",
    },
    baseButtonClass: {
      type: [Array, Object, String],
      default: "",
    },
  },
  emits: ["tags-changed"],
  setup(props) {
    const streamGroupsStore = useStreamGroupsStore();

    const { addStreamGroup } = streamGroupsStore;

    const { currentRouteProductMenu } = useProductMenu();

    const { selectedTeamPreference } = useTeamPicker();

    const {
      nextSocialBookmarkGroupPosition,
      nextTraditionalBookmarkGroupPosition,
    } = storeToRefs(streamGroupsStore);

    const {
      label,
      groupId,
      bookmarksForMention,

      updateBookmarkStreamsForMention,
    } = useBookmarks(props);

    return {
      addStreamGroup,
      nextSocialBookmarkGroupPosition,
      nextTraditionalBookmarkGroupPosition,
      currentRouteProductMenu,
      selectedTeamPreference,
      label,
      groupId,
      bookmarksForMention,

      updateBookmarkStreamsForMention,
    };
  },
  data() {
    return {
      placement: "right",
      close: false,
      disableOffset: false,
    };
  },
  computed: {
    actionLabel() {
      if (this.isSocial)
        return this.$t("bookmark_stream_actions.tag_social_item");

      return this.$t("bookmark_stream_actions.tag");
    },
    isSocialPage() {
      return this.currentRouteProductMenu === "social";
    },
    nextGroupPosition() {
      return this.isSocialPage
        ? this.nextSocialBookmarkGroupPosition
        : this.nextTraditionalBookmarkGroupPosition;
    },
  },
  watch: {
    bookmarksForMention: {
      deep: true,
      handler(data) {
        this.$emit("tags-changed", data);
      },
    },
  },
  methods: {
    async addBookmarkStreamToGroup(data) {
      try {
        const newGroup = (
          await this.addStreamGroup({
            params: {
              stream_type: streamTypes.bookmarkStream,
              label: data.groupLabel,
              color: data.groupColor,
              social_bookmarks_stream: this.isSocialPage,
              ...(this.$features.has("has_team_workspaces")
                ? { organisation_team_id: this.selectedTeamPreference }
                : {}),
              position: this.nextGroupPosition,
            },
          })
        ).data;

        this.groupId = newGroup.id;
        this.label = data.streamLabel;
        this.updateBookmarkStreams();
      } catch (error) {
        this.$error(this.$t("bookmark_stream_actions.error_message"), error);
      }
    },
    moveToExistingGroup(data) {
      this.label = data.streamLabel;
      this.groupId = data.existingGroupId;
      this.updateBookmarkStreams();
    },
    async updateBookmarkStreams(bookmarkStream) {
      try {
        this.$q.loading.show();
        await this.updateBookmarkStreamsForMention(bookmarkStream);
      } catch (error) {
        this.$error(this.$t("bookmark_stream_actions.error_message"), error);
      } finally {
        this.$q.loading.hide();
      }
    },
    calculatePlacement() {
      this.offsetPlacement = false;
      if (!this.$refs.button) return;

      const rects = this.$refs.button.$el.getBoundingClientRect();

      const placement = [
        rects.right + rects.width > document.body.clientWidth
          ? "left"
          : "right",
      ];

      if (document.body.clientHeight - rects.top < 300) {
        placement.push("end");
        this.disableOffset = true;
      }

      this.placement = placement.join("-");
    },
    cancel() {
      this.close = true;
    },
    onHide() {
      this.close = false;
    },
  },
};
</script>
